// src/views/welcome.js
import React, { useEffect, useMemo, useState } from "react";
import "./welcome.css";
import "./common-view.css";
import { useTranslation } from "react-i18next";
import { BACKEND_BASE_URL } from "../config/appUrls";

const Welcome = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading"); // loading | success | error
  const [sessionInfo, setSessionInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");

  const sessionId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("session_id");
  }, []);

  useEffect(() => {
    const run = async () => {
      if (!sessionId) {
        setStatus("error");
        setErrorMsg(t("welcome.errors.missingSession"));
        return;
      }

      try {
        const url = `${BACKEND_BASE_URL}/api/stripe/session?session_id=${encodeURIComponent(sessionId)}`;

        const res = await fetch(url, { method: "GET" });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw new Error(data?.error || t("welcome.errors.verifyFailed"));
        }

        setSessionInfo(data);
        setStatus("success");
      } catch (err) {
        setStatus("error");
        setErrorMsg(err?.message || t("welcome.errors.unableVerify"));
      }
    };

    run();
  }, [sessionId, t]);

  return (
    <div className="welcome-page">
      <div className="welcome-card">
        {status === "loading" && (
          <div className="welcome-state-box">
            <div className="welcome-loader" />
            <h1 className="welcome-title">{t("welcome.loadingTitle")}</h1>
            <p className="welcome-subtitle">{t("welcome.loadingSubtitle")}</p>
          </div>
        )}

        {status === "success" && (
          <>
            <div className="welcome-icon-badge success">✅</div>
            <h1 className="welcome-title">{t("welcome.successTitle")}</h1>
            <p className="welcome-subtitle">{t("welcome.successSubtitle")}</p>

            <div className="welcome-details">
              {sessionInfo?.customer_email && (
                <div className="welcome-row">
                  <span className="welcome-label">{t("welcome.labels.email")}</span>
                  <span className="welcome-value">{sessionInfo.customer_email}</span>
                </div>
              )}
              {sessionInfo?.subscription_id && (
                <div className="welcome-row">
                  <span className="welcome-label">{t("welcome.labels.subscription")}</span>
                  <span className="welcome-value">{sessionInfo.subscription_id}</span>
                </div>
              )}
              {sessionInfo?.payment_status && (
                <div className="welcome-row">
                  <span className="welcome-label">{t("welcome.labels.paymentStatus")}</span>
                  <span className="welcome-value">{sessionInfo.payment_status}</span>
                </div>
              )}
            </div>

            <div className="welcome-next">
              <h2 className="welcome-h2">{t("welcome.nextStepsTitle")}</h2>
              <ol className="welcome-steps">
                <li>{t("welcome.steps.tutorial")}</li>
                <li>{t("welcome.steps.dashboard")}</li>
              </ol>
            </div>

            <div className="welcome-actions">
              <a className="btn-welcome-primary" href="/dashboard">
                {t("welcome.actions.dashboard")} →
              </a>
              <a className="btn-welcome-secondary" href="/">
                <span className="btn-back-icon">←</span>
                <span>{t("welcome.actions.home")}</span>
              </a>
            </div>

            <p className="welcome-footnote">{t("welcome.footnote")}</p>
          </>
        )}

        {status === "error" && (
          <>
            <div className="welcome-icon-badge error">⚠️</div>
            <h1 className="welcome-title">{t("welcome.errors.confirmFailed")}</h1>
            <p className="welcome-subtitle">
              {errorMsg || t("welcome.errors.generic")}
            </p>

            <div className="welcome-actions">
              <a className="btn-welcome-primary" href="/">
                <span className="btn-back-icon">←</span>
                <span>{t("welcome.actions.returnHome")}</span>
              </a>
              <a className="btn-welcome-secondary" href="/dashboard?tab=onboarding">
                {t("welcome.actions.tryAgain", "Try again")}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Welcome;